/**
 * ContractHub — the Contingency Hub for a Wisconsin deal (design §5.3).
 *
 * Chosen when the transaction has a `contract_tracking` block; every other
 * state keeps the existing hub. One card per contingency, in the offer's
 * order and keyed by id, plus the notice composer, the timeline and, once
 * the offer has ended, the neutral termination view.
 *
 * When `contract_tracking.notices_available` is false (before the notices
 * migration) the cards are read-only: no notice or flag actions.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useContingencyEvents } from '../../../agents/contingencyNoticesAgent';
import ContingencyContractCard from './ContingencyContractCard';
import { contractCopy } from './contractCopy';
import NoticeComposerModal from './NoticeComposerModal';
import TerminatedDealPanel from './TerminatedDealPanel';
import styles from './ContractHub.module.css';

/** True when this transaction uses the Wisconsin contract hub. */
export const usesContractHub = (transaction) => Boolean(transaction?.contract_tracking);

/**
 * @param {{transaction: object, role: ('buyer'|'seller'), onRefresh?: Function,
 *   focusContingencyId?: (number|null)}} props
 *   `focusContingencyId`: the card a notification opened the hub on; it is
 *   scrolled to and focused once, when that card is on this transaction.
 */
export default function ContractHub({ transaction, role, onRefresh, focusContingencyId = null }) {
  const hubRef = useRef(null);
  const focusedIdRef = useRef(null);
  const tracking = transaction.contract_tracking || {};
  const [refreshKey, setRefreshKey] = useState(0);
  const [composer, setComposer] = useState(null);
  const { events, loading, error, reload } = useContingencyEvents(transaction.id, refreshKey);
  // The offer's order: rows are seeded in that order, so by id. The
  // transaction payload itself doesn't keep it.
  const rows = (transaction.contingencies || [])
    .filter((c) => c.contract)
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  const dealEnded = Boolean(tracking.terminated);
  const windDownPending = Boolean(tracking.terminated?.wind_down_pending);
  const noticesAvailable = Boolean(tracking.notices_available);

  const hasFocusRow = focusContingencyId != null && rows.some((c) => c.id === focusContingencyId);

  useEffect(() => {
    if (!hasFocusRow || focusedIdRef.current === focusContingencyId) return;
    const card = hubRef.current?.querySelector(`[data-testid="contract-card-${focusContingencyId}"]`);
    if (!card) return;
    focusedIdRef.current = focusContingencyId;
    if (typeof card.scrollIntoView === 'function') card.scrollIntoView({ block: 'start', behavior: 'smooth' });
    card.focus({ preventScroll: true });
  }, [hasFocusRow, focusContingencyId]);

  const refreshAll = useCallback(() => {
    setRefreshKey((k) => k + 1);
    onRefresh?.();
  }, [onRefresh]);

  const openComposer = useCallback((contingency, type) => setComposer({ contingency, type }), []);

  const onDelivered = useCallback(() => {
    setComposer(null);
    refreshAll();
  }, [refreshAll]);

  return (
    <div ref={hubRef} className={styles.hub} data-testid="contract-hub" data-role={role}>
      {dealEnded && <TerminatedDealPanel transaction={transaction} events={events} />}

      <p className={styles.note} role="note">{contractCopy('WI.hub.not_tracked_note')}</p>
      {!noticesAvailable && !dealEnded && (
        <p className={styles.note} data-testid="notices-unavailable">
          {contractCopy('WI.hub.notices_unavailable')}
        </p>
      )}

      {rows.length === 0 ? (
        <p className={styles.empty}>No contingencies from your signed offer are tracked yet.</p>
      ) : (
        <div className={styles.cards}>
          {rows.map((c) => (
            <ContingencyContractCard
              key={c.id}
              contingency={c}
              role={role}
              events={events}
              eventsLoading={loading}
              eventsError={error}
              onRetryEvents={reload}
              noticesAvailable={noticesAvailable}
              dealEnded={dealEnded}
              windDownPending={windDownPending}
              onDeliver={openComposer}
              onChanged={refreshAll}
              onRefresh={onRefresh}
              focused={c.id === focusContingencyId}
            />
          ))}
        </div>
      )}

      {composer && (
        <NoticeComposerModal
          contingency={composer.contingency}
          role={role}
          initialType={composer.type}
          onClose={() => setComposer(null)}
          onDelivered={onDelivered}
        />
      )}
    </div>
  );
}
